import { useTranslation } from 'react-i18next';

import ModalComponent from '@/components/molecules/ModalComponent/ModalComponent';
import {
    type AuditDetailRow,
    type AuditFilterChange,
    type AuditLogEntry,
    type AuditLogFilters,
    addAuditFilters,
    describeAuditChanges,
    describeAuditDetails,
    useAuditText,
} from '@/domain/auditLog';

import ChangesTable from './details/ChangesTable';
import DetailsHeader from './details/DetailsHeader';
import DetailsRow from './details/DetailsRow';
import DetailsSection from './details/DetailsSection';
import RawJson from './details/RawJson';
import RequestDataList from './details/RequestDataList';

import styles from './AuditLogDetails.module.scss';

interface AuditLogDetailsProps {
    entry: AuditLogEntry;
    /** Loaded entries sharing the entry's request, itself included. */
    requestCount: number;
    /** A filter button or a pivot. The dialog closes after it, onto the filtered list. */
    onFilter: (change: AuditFilterChange) => void;
    onClose: () => void;
}

interface BodyProps {
    entry: AuditLogEntry;
    requestCount: number;
    onFilter: (change: AuditFilterChange) => void;
}

interface RowListProps {
    rows: readonly AuditDetailRow[];
    onFilter: (filters: AuditLogFilters) => void;
}

function RowList({ rows, onFilter }: RowListProps) {
    const render = useAuditText();
    return (
        <dl className={styles.rows}>
            {rows.map((row) => {
                const { filter } = row;
                return (
                    <DetailsRow
                        key={row.id}
                        label={render(row.label)}
                        value={row.value === null ? null : render(row.value)}
                        note={row.note === null ? null : render(row.note)}
                        filterLabel={row.filterLabel === null ? null : render(row.filterLabel)}
                        onFilter={filter === null ? undefined : () => onFilter(filter)}
                    />
                );
            })}
        </dl>
    );
}

function ChangesContent({
    entry,
    onFilter,
}: Pick<RowListProps, 'onFilter'> & { entry: AuditLogEntry }) {
    const { t } = useTranslation();
    const changes = describeAuditChanges(entry.changes);
    if (changes.kind === 'table') return <ChangesTable rows={changes.rows} onFilter={onFilter} />;
    return (
        <p className={styles.none}>
            {changes.kind === 'none' ? t('auditLog.details.none') : t('auditLog.details.rawOnly')}
        </p>
    );
}

function DetailsBody({ entry, requestCount, onFilter }: BodyProps) {
    const { t } = useTranslation();
    const sections = describeAuditDetails(entry);
    const { changes, metadata } = entry;
    const addFilters = (filters: AuditLogFilters) => onFilter(addAuditFilters(filters));

    return (
        <div className={styles.body}>
            <DetailsHeader entry={entry} requestCount={requestCount} onFilter={onFilter} />
            <DetailsSection title={t('auditLog.details.section.who')}>
                <RowList rows={sections.who} onFilter={addFilters} />
            </DetailsSection>
            <DetailsSection title={t('auditLog.details.section.target')}>
                <RowList rows={sections.target} onFilter={addFilters} />
            </DetailsSection>
            <DetailsSection title={t('auditLog.details.section.changes')}>
                <ChangesContent entry={entry} onFilter={addFilters} />
            </DetailsSection>
            <DetailsSection title={t('auditLog.details.section.requestData')}>
                {metadata === null ? (
                    <p className={styles.none}>{t('auditLog.details.none')}</p>
                ) : (
                    <RequestDataList metadata={metadata} onFilter={addFilters} />
                )}
            </DetailsSection>
            <DetailsSection title={t('auditLog.details.section.request')}>
                <RowList rows={sections.request} onFilter={addFilters} />
            </DetailsSection>
            {changes === null ? null : (
                <RawJson summary={t('auditLog.details.rawChanges')} value={changes} />
            )}
            {metadata === null ? null : (
                <RawJson summary={t('auditLog.details.rawMetadata')} value={metadata} />
            )}
        </div>
    );
}

/**
 * The technical detail behind a row and the full form of everything the table cuts
 * short, every value with a button that filters by it.
 */
export default function AuditLogDetails(props: AuditLogDetailsProps) {
    const { entry, requestCount, onFilter, onClose } = props;
    const { t } = useTranslation();

    function handleFilter(change: AuditFilterChange) {
        onFilter(change);
        onClose();
    }

    return (
        <ModalComponent
            isOpen
            onClose={onClose}
            size="lg"
            title={t('auditLog.details.title')}
            body={<DetailsBody entry={entry} requestCount={requestCount} onFilter={handleFilter} />}
        />
    );
}
